import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import { InputText, InputTextArea, InputRadio } from '../../../atoms'
import { ConnectionVariablesDescription } from 'components/atoms/ConnectionVariablesDescription'
import { agentService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import AgentLayoutNew from '../_layouts/new'

declare var window: interfaces.IWindow

interface ISelectOption {
  value: string
  label: string
}

const title = I18n.t('agent.create_new', {
  model: I18n.t('connection_mail_template', { scope: 'activerecord.models' }),
})

const FIELDS = {
  title: 'title',
  mail_subject: 'mail_subject',
  mail_text: 'mail_text',
  guide_mail_subject: 'guide_mail_subject',
  guide_mail_text: 'guide_mail_text',
  blank_item: 'blank_item',
}

const createBlankItemOptions = (blankItems: { [key: string]: string }): ISelectOption[] => {
  return Object.entries(blankItems).map(([key]) => ({
    value: key,
    label: I18n.t(`connection.blank_item.${key}`),
  }))
}

interface IProps {
  currentUser: interfaces.IUser
  enumData: {
    blank_items: { [key: string]: string }
  }
}

const AgentConnectionMailTemplateNew: React.FC<IProps> = props => {
  const blankItemOptions = createBlankItemOptions(props.enumData.blank_items)
  const [selectedBlankItem, setSelectedBlankItem] = React.useState(blankItemOptions[0])
  const [creationMode, setCreationMode] = React.useState('shared')

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const submitData = { ...values }
      if (creationMode === 'shared' && selectedBlankItem.value !== 'nothing') {
        submitData.guide_mail_subject = values.mail_subject
        submitData.guide_mail_text = values.mail_text
      }

      const { createdTemplate, flush } = await agentService.createConnectionMailTemplate({
        ...submitData,
        blank_item: selectedBlankItem.value,
      })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/agent/connection_mail_templates'
    },
    [selectedBlankItem, creationMode]
  )

  const updateSelectedBlankItem = (option: ISelectOption) => {
    if (option) {
      setSelectedBlankItem(option)
      if (option.value === 'nothing') {
        setCreationMode('shared')
      }
    }
  }

  return (
    <AgentLayoutNew
      model="connection_mail_template"
      title={title}
      fields={FIELDS}
      indexLink="/agent/connection_mail_templates"
      formItems={
        <>
          <S.FormItemWrapper>
            <S.ReactSelectWrapper>
              <p className="ReactSelect_Title">
                Blank Item<span className="InputText_Required">{I18n.t('generic.required')}</span>
              </p>
              <div className="ReactSelect_Main">
                <ReactSelect
                  name="blank_item"
                  placeholder="Select Blank Item"
                  required={true}
                  options={blankItemOptions}
                  onChange={updateSelectedBlankItem}
                  value={selectedBlankItem}
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                />
              </div>
            </S.ReactSelectWrapper>
          </S.FormItemWrapper>
          <S.FormItemWrapper>
            <S.CreationModeGroup>
              <InputRadio
                label="作成方式"
                required={true}
                name="creation_mode"
                defaultValue={creationMode} // ハック的な対応なので、今後valueによる制御を追加したい
                options={[
                  { id: 'shared', value: 'shared', label: 'ゲスト・ガイド同じ内容' },
                  { id: 'separate', value: 'separate', label: 'ゲスト・ガイド異なる内容' },
                ]}
                disabled={selectedBlankItem.value === 'nothing'}
                onChangeHandler={e => setCreationMode(e.target.value)}
              />
            </S.CreationModeGroup>
          </S.FormItemWrapper>
          <S.FormItemWrapper>
            <InputText required={true} name="title" label={I18n.t('generic.name')} />
          </S.FormItemWrapper>

          <S.SeparateModeSection>
            <S.SectionTitle>
              {creationMode === 'shared' ? 'ゲスト・ガイド用' : 'ゲスト用'}
            </S.SectionTitle>
            <S.FormItemWrapper>
              <InputText
                required={true}
                name="mail_subject"
                label={I18n.t('mail_template.subject')}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea required={true} name="mail_text" label={I18n.t('generic.text')} />
            </S.FormItemWrapper>
          </S.SeparateModeSection>

          {creationMode === 'separate' ? (
            <S.SeparateModeSection>
              <S.SectionTitle>ガイド用</S.SectionTitle>
              <S.FormItemWrapper>
                <InputText
                  required={true}
                  name="guide_mail_subject"
                  label={I18n.t('mail_template.subject')}
                />
              </S.FormItemWrapper>
              <S.FormItemWrapper>
                <InputTextArea
                  required={true}
                  name="guide_mail_text"
                  label={I18n.t('generic.text')}
                />
              </S.FormItemWrapper>
            </S.SeparateModeSection>
          ) : (
            <>
              <input type="hidden" name="guide_mail_subject" value={''} />
              <input type="hidden" name="guide_mail_text" value={''} />
            </>
          )}
          <S.FormItemWrapper>
            <S.VariableDescriptionWrapper>
              <ConnectionVariablesDescription />
            </S.VariableDescriptionWrapper>
          </S.FormItemWrapper>
        </>
      }
      handleSubmit={handleSubmit}
    />
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.CreationModeGroup = styled.div`
  .CreationMode_Title {
    font-size: 15px;
    font-weight: bold;
    margin-bottom: 12px;

    .InputText_Required {
      display: inline-block;
      border-radius: 3px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.caution};
      vertical-align: middle;
      font-size: 11px;
      font-weight: normal;
      padding: 4px 6px;
      margin-left: 8px;
    }
  }
`

S.RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 14px;

    input[type='radio'] {
      margin-right: 8px;
      cursor: pointer;
    }
  }
`

S.SeparateModeSection = styled.div`
  margin-top: 16px;
  padding: 16px 0;
`

S.SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  padding-bottom: 8px;
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 180px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    .InputText_Required {
      display: inline-block;
      border-radius: 3px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.caution};
      vertical-align: middle;
      font-size: 11px;
      font-weight: normal;
      padding: 4px 6px;
      margin-left: 8px;
    }

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

S.VariableDescriptionWrapper = styled.div`
  margin-top: 40px;
  padding-top: 16px;
  border-top: 2px solid ${constants.COLORS.divider2};
`

export default AgentConnectionMailTemplateNew
